import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { useTranslate } from "@tolgee/react";
import { FileX } from "lucide-react";

import { ReactComponent as OptionBtn } from "../../Assets/iconsv2/controlButtons/optionBtn.svg";
import { ReactComponent as OptionBtnLight } from "../../Assets/iconsv2/controlButtons/optionBtnLight.svg";
import { ReactComponent as RecordCamera } from "../../Assets/iconsv2/controlButtons/RecordCamera.svg";
import { ReactComponent as RecordCameraLight } from "../../Assets/iconsv2/controlButtons/RecordCameraLight.svg";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import WarningConfirmationModal from "../GenericComponents/WarningConfirmationModal";

const MeetingOptionsButton = ({
  isMeeting = true,
  meetingId,
  aiSummaryEnabled,
  transcriptExportEnabled,
  onToggleAiSummary,
  onToggleTranscriptExport,
  isRecording = false,
  isRecordingLoading = false,
  handleRecordBtn,
  disabled = false,
  chatToggleEnabled = false,
  chatEnabled = true,
  onToggleChat,
}) => {
  const { t } = useTranslate();
  const { theme } = useGenericComponentsStateService();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingStopSetting, setPendingStopSetting] = useState(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    middleware: [
      offset(10),
      flip({ fallbackAxisSideDirection: "end" }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleAiSummaryToggle = () => {
    if (aiSummaryEnabled) {
      setPendingStopSetting('aiSummary');
      return;
    }
    onToggleAiSummary(true);
  };

  const handleTranscriptToggle = () => {
    if (transcriptExportEnabled) {
      setPendingStopSetting('transcript');
      return;
    }
    onToggleTranscriptExport(true);
  };

  const handleConfirmStopSetting = () => {
    if (pendingStopSetting === 'aiSummary') {
      onToggleAiSummary(false);
    } else if (pendingStopSetting === 'transcript') {
      onToggleTranscriptExport(false);
    }
    setPendingStopSetting(null);
  };

  const stopModalContent = {
    transcript: {
      title: t('call_modal.stop_transcript_export_title'),
      message: t('call_modal.stop_transcript_export_message'),
    },
    aiSummary: {
      title: t('call_modal.stop_ai_summary_title'),
      message: t('call_modal.stop_ai_summary_message'),
    },
  }[pendingStopSetting] || {};

  return (
    <Container>
      <WarningConfirmationModal
        open={!!pendingStopSetting}
        icon={<FileX size={26} />}
        title={stopModalContent.title}
        message={stopModalContent.message}
        confirmLabel={t('call_modal.stop_confirm')}
        onCancel={() => setPendingStopSetting(null)}
        onConfirm={handleConfirmStopSetting}
      />

      <Wrapper>
        <CustomButton
          ref={refs.setReference}
          {...getReferenceProps()}
          disabled={disabled}
        >
          <IconWrapper>
            {theme === "light" ? <OptionBtnLight /> : <OptionBtn />}
          </IconWrapper>
        </CustomButton>
        <CustomLabel>{t("call_modal.more", "More")}</CustomLabel>
      </Wrapper>

      {isOpen && (
        <FloatingPortal>
        <FloatingFocusManager context={context} modal={false}>
          <FlyoutContainer
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >

            {isMeeting && (
              <ActionRow
                onClick={handleRecordBtn}
              >
                <ActionIconWrapper $isRecording={isRecording}>
                  {isRecordingLoading ? (
                    <Spinner />
                  ) : theme === "light" ? (
                    <RecordCameraLight width={20} height={20} />
                  ) : (
                    <RecordCamera width={20} height={20} />
                  )}
                </ActionIconWrapper>
                <ActionLabel>
                  {isRecording
                    ? t("call_modal.stop_record", "Stop Recording")
                    : t("call_modal.start_record", "Start Recording")}
                </ActionLabel>
              </ActionRow>
            )}

            {chatToggleEnabled && (
              <OptionRow onClick={() => onToggleChat(!chatEnabled)}>
                <OptionInfo>
                  <OptionTitle>{t("call_modal.toggle_chat_title")}</OptionTitle>
                  <OptionDescription>
                    {t("call_modal.toggle_chat_description")}
                  </OptionDescription>
                </OptionInfo>
                <ToggleSwitch $enabled={chatEnabled}>
                  <ToggleKnob $enabled={chatEnabled} />
                </ToggleSwitch>
              </OptionRow>
            )}

            {isMeeting && (
              <OptionRow onClick={handleTranscriptToggle}>
                <OptionInfo>
                  <OptionTitle>{t("call_modal.start_transcription")}</OptionTitle>
                  <OptionDescription>
                    {t("call_modal.transcript_export_description")}
                  </OptionDescription>
                </OptionInfo>
                <ToggleSwitch $enabled={transcriptExportEnabled}>
                  <ToggleKnob $enabled={transcriptExportEnabled} />
                </ToggleSwitch>
              </OptionRow>
            )}

            {isMeeting && (
              <OptionRow onClick={handleAiSummaryToggle}>
                <OptionInfo>
                  <OptionTitle>{t("call_modal.ai_summary")}</OptionTitle>
                  <OptionDescription>
                    {t("call_modal.ai_summary_description")}
                  </OptionDescription>
                </OptionInfo>
                <ToggleSwitch $enabled={aiSummaryEnabled}>
                  <ToggleKnob $enabled={aiSummaryEnabled} />
                </ToggleSwitch>
              </OptionRow>
            )}
          </FlyoutContainer>
        </FloatingFocusManager>
        </FloatingPortal>
      )}
    </Container>
  );
};

export default MeetingOptionsButton;

const Container = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  min-height: 43px;
  align-items: center;
  gap: 4px;
`;

const CustomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  width: 50px;
  border-radius: 100%;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  overflow: hidden;
  background: transparent;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  svg {
    color: ${({ theme }) =>
      theme.callComponent.controlButtonBackgroundActive};
    fill: ${({ theme }) =>
      theme.callComponent.controlButtonIconActive};
  }
`;

const CustomLabel = styled.label`
  font-size: 10px;
  color: ${({ theme }) => theme.callComponent.controlButtonTextColor};
  line-height: 12px;
  text-align: center;
`;

const FlyoutContainer = styled.div`
  background: ${({ theme }) => theme.menu.background};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 12px;
  padding: 10px 16px 16px;
  width: 280px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};

  &:hover {
    opacity: 0.8;
  }
`;

const ActionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: ${({ $isRecording, theme }) =>
      $isRecording ? "#ef4444" : theme.menu.primaryTextColor};
    stroke: ${({ $isRecording, theme }) =>
      $isRecording ? "#ef4444" : theme.menu.primaryTextColor};
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: ${({ theme }) => theme.menu.loadingIndicator};
  border-right-color: ${({ theme }) => theme.menu.loadingIndicator};
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const ActionLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-of-type {
    padding-top: 0;
  }
`;

const OptionInfo = styled.div`
  flex: 1;
  margin-right: 12px;
`;

const OptionTitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0 0 4px 0;
`;

const OptionDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
`;

const ToggleSwitch = styled.div`
  width: 44px;
  height: 24px;
  background: ${({ $enabled, $disabled, theme }) =>
    $disabled
      ? theme.callComponent.translationModeBg
      : $enabled
      ? "#10b981"
      : theme.callComponent.translationModeBg};
  border-radius: 12px;
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleKnob = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: ${({ $enabled }) => ($enabled ? "22px" : "2px")};
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;
